import { useCallback, useEffect, useRef, useState } from "react";
import type { RefObject } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { projectCategories, otherWorkItems } from "../../mocks/projects";
import { useScrollReveal, useDrawLine } from "../../hooks/useScrollReveal";
import ProjectCard from "../projects/components/ProjectCard";

export default function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const homeRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const resumeRef = useRef<HTMLDivElement>(null);
  const projectsRef = useRef<HTMLDivElement>(null);
  const otherWorkRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);

  // ... existing code ...

  // ── Hero ──────────────────────────────────────────────────────────────
  const heroPortrait = useScrollReveal<HTMLDivElement>({ delay: 80,  direction: "left",  distance: 40, duration: 900 });
  const heroText    = useScrollReveal<HTMLDivElement>({ delay: 220, direction: "up",    distance: 30, duration: 800 });
  const heroTagline = useScrollReveal<HTMLDivElement>({ delay: 380, direction: "right", distance: 30, duration: 800 });
  const heroRule    = useDrawLine<HTMLDivElement>({ delay: 600, duration: 1000 });

  // ── Section dividers ─────────────────────────────────────────────────
  const divider2 = useDrawLine<HTMLDivElement>({ delay: 100, duration: 900 });
  const divider3 = useDrawLine<HTMLDivElement>({ delay: 100, duration: 900 });
  const divider4 = useDrawLine<HTMLDivElement>({ delay: 100, duration: 900 });

  // ── Hero→About transition band ────────────────────────────────────────
  const transitionBandRef = useScrollReveal<HTMLDivElement>({ delay: 0, direction: "none", duration: 800 });
  const transitionQuote   = useScrollReveal<HTMLParagraphElement>({ delay: 200, direction: "up", distance: 20, duration: 700 });
  const transitionScroll  = useScrollReveal<HTMLDivElement>({ delay: 420, direction: "none", duration: 600 });

  // bouncing scroll cue
  const [bounce, setBounce] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setBounce(b => !b), 900);
    return () => clearInterval(t);
  }, []);

  // ── About ─────────────────────────────────────────────────────────────
  const aboutLabel  = useScrollReveal<HTMLDivElement>({ delay: 80,  direction: "left",  distance: 24, duration: 700 });
  const aboutText   = useScrollReveal<HTMLDivElement>({ delay: 200, direction: "up",    distance: 28, duration: 750 });

  // ── Resume ────────────────────────────────────────────────────────────
  const resumeLabel = useScrollReveal<HTMLDivElement>({ delay: 80,  direction: "left",  distance: 24, duration: 700 });
  const resumeRow   = useScrollReveal<HTMLDivElement>({ delay: 200, direction: "up",    distance: 28, duration: 750 });

  // ── Projects ──────────────────────────────────────────────────────────
  const projectsLabel = useScrollReveal<HTMLDivElement>({ delay: 80,  direction: "left",  distance: 24, duration: 700 });
  const projectsLine  = useDrawLine<HTMLDivElement>({ delay: 260, duration: 900 });

  // ── Other Work ────────────────────────────────────────────────────────
  const otherWorkLabel = useScrollReveal<HTMLDivElement>({ delay: 80,  direction: "left",  distance: 24, duration: 700 });
  const otherWorkLine  = useDrawLine<HTMLDivElement>({ delay: 260, duration: 900 });
  const otherWorkItem  = useScrollReveal<HTMLDivElement>({ delay: 200, direction: "up",    distance: 20, duration: 700 });

  const allProjects = projectCategories.flatMap((cat) =>
    cat.projects.map((p) => ({ ...p, categorySlug: cat.slug }))
  );

  // ── Contact ───────────────────────────────────────────────────────────
  const contactLabel   = useScrollReveal<HTMLDivElement>({ delay: 80,  direction: "left",  distance: 24, duration: 700 });
  const contactPills   = useScrollReveal<HTMLDivElement>({ delay: 220, direction: "up",    distance: 28, duration: 750 });

  const scrollToSection = useCallback((id: string, behavior: ScrollBehavior = "smooth") => {
    const refs: Record<string, RefObject<HTMLDivElement | null>> = {
      home: homeRef,
      about: aboutRef,
      resume: resumeRef,
      projects: projectsRef,
      "other-work": otherWorkRef,
      contact: contactRef,
    };
    const ref = refs[id];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior, block: "start" });
    }
  }, []);

  useEffect(() => {
    if (location.hash) {
      window.requestAnimationFrame(() => scrollToSection(location.hash.slice(1), "auto"));
    }
  }, [location.hash, scrollToSection]);

  const handleNavigate = useCallback(
    (id: string) => {
      scrollToSection(id);
    },
    [scrollToSection]
  );

  return (
    <div className="min-h-screen bg-off-white">
      <Navbar onNavigate={handleNavigate} />

      {/* ===== HERO ===== */}
      <section
        ref={homeRef}
        id="home"
        className="w-full pt-32 pb-16 px-6 md:px-12 lg:px-20 bg-warm-beige relative overflow-hidden"
      >
        {/* Teal background accents */}
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-teal/8 blur-3xl pointer-events-none" />
        <div className="absolute bottom-8 left-1/4 w-48 h-48 rounded-full bg-teal/5 blur-2xl pointer-events-none" />

        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center gap-8 lg:gap-10 relative">
          {/* Portrait — slides in from left */}
          <div
            ref={heroPortrait}
            className="relative flex-shrink-0 w-40 h-52 sm:w-44 sm:h-56 rounded-lg overflow-hidden"
            style={{
              maskImage: "radial-gradient(ellipse 92% 92% at center, black 60%, transparent 100%)",
              WebkitMaskImage: "radial-gradient(ellipse 92% 92% at center, black 60%, transparent 100%)",
            }}
          >
            <img
              src="https://static.readdy.ai/image/ea42db9d790411a3487528cbb67c49ce/f9ebb261875086bf1b3bdd1ef2db7a4f.jpeg"
              alt="Jordan Duke"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute bottom-0 left-0 w-14 h-[2px] bg-teal/80" />
            <div className="absolute bottom-0 left-0 w-[2px] h-14 bg-teal/80" />
            <div className="absolute top-0 right-0 w-8 h-[2px] bg-teal/50" />
            <div className="absolute top-0 right-0 w-[2px] h-8 bg-teal/50" />
          </div>

          {/* Name + role — slides up */}
          <div ref={heroText} className="relative sm:h-56 flex flex-col justify-center pb-7 sm:pb-0">
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-charcoal font-light leading-none tracking-tight">
              Jordan Duke
            </h1>
            <p className="mt-4 font-sans text-xs uppercase tracking-[4px] text-terracotta">
              Project Manager, Senior Interior Designer
            </p>
            <div className="mt-3 sm:mt-0 sm:absolute sm:bottom-0 left-0 flex items-center gap-2">
              <div className="w-12 h-[2px] bg-teal/70" />
              <div className="w-4 h-[2px] bg-teal/40" />
              <div className="w-2 h-[2px] bg-teal/20" />
            </div>
          </div>
        </div>

        {/* Animated rule */}
        <div className="max-w-6xl mx-auto mt-12 overflow-hidden">
          <div
            ref={heroRule}
            style={{ width: "0%" }}
            className="h-[2px] bg-gradient-to-r from-terracotta/30 via-teal/60 to-teal/10"
          />
        </div>
      </section>

      {/* ── Hero → About Transition Band ── */}
      <div
        ref={transitionBandRef}
        className="relative w-full bg-warm-beige overflow-hidden"
      >
        {/* Diagonal clip bottom edge */}
        <div
          className="absolute bottom-0 left-0 right-0 h-12 bg-off-white"
          style={{ clipPath: "polygon(0 100%, 100% 100%, 100% 0)" }}
        />
        {/* Teal accent orbs */}
        <div className="absolute left-0 top-0 w-64 h-full bg-gradient-to-r from-teal/6 to-transparent pointer-events-none" />
        <div className="absolute right-0 top-0 w-48 h-full bg-gradient-to-l from-terracotta/5 to-transparent pointer-events-none" />

        <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20 py-10 flex flex-col items-center gap-5 relative">
          {/* Decorative top rule */}
          <div className="flex items-center gap-3 w-full max-w-md">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent to-teal/40" />
            <div className="w-1.5 h-1.5 rounded-full bg-teal/60" />
            <div className="flex-1 h-px bg-gradient-to-l from-transparent to-teal/40" />
          </div>

          {/* Pull quote */}
          <p
            ref={transitionQuote}
            className="font-serif text-lg md:text-xl text-charcoal/50 italic text-center max-w-lg leading-relaxed tracking-wide"
          >
            &ldquo;The details are not the details. They make the design.&rdquo;<br />— Charles Eames
          </p>

          {/* Scroll cue */}
          <div
            ref={transitionScroll}
            className="flex flex-col items-center gap-1.5"
          >
            <span className="font-sans text-[10px] uppercase tracking-[3px] text-teal/50">
              About
            </span>
            <div
              className="w-5 h-5 flex items-center justify-center text-teal/50"
              style={{
                transform: bounce ? "translateY(4px)" : "translateY(0px)",
                transition: "transform 0.4s ease-in-out",
              }}
            >
              <i className="ri-arrow-down-s-line text-base" />
            </div>
          </div>
        </div>
      </div>

      {/* ===== ABOUT ===== */}
      <section
        ref={aboutRef}
        id="about"
        className="bg-off-white py-16 md:py-20 px-6 md:px-12 lg:px-20"
      >
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-8 lg:gap-20">
          {/* Label — slides in from left */}
          <div ref={aboutLabel}>
            <p className="font-sans text-xs uppercase tracking-[2px] text-terracotta">
              About
            </p>
            <div className="mt-2 w-5 h-[2px] bg-teal/60" />
          </div>

          {/* Body text — slides up */}
          <div
            ref={aboutText}
            className="space-y-5 font-serif text-base md:text-lg text-charcoal/80 leading-[1.8] border-l-[3px] border-teal/40 pl-6"
          >
            <p>
              With over a decade of experience, I create hospitality-inspired
              multifamily and workplace spaces that balance atmosphere,
              functionality, and seamless technical integration from early concept
              through completion.
            </p>
            <p>
              I specialize in translating ambitious, highly custom design ideas into
              buildable, budget-conscious solutions. My work is especially focused
              on the details that give a space its character — materiality,
              proportion, custom elements, and the creative construction solutions
              that protect the design intent without losing sight of real-world
              constraints.
            </p>
            <p>
              Since earning my Bachelor of Science degree in Interior Design from
              Auburn University, my work has been recognized by the Commercial
              Interior Design Association, the American Society of Interior
              Designers, and the Urban Land Institute.
            </p>
          </div>
        </div>
      </section>

      {/* ===== IMPACT SNAPSHOT ===== */}
      <section className="bg-off-white pb-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-8 lg:gap-20">
          <div>
            <p className="font-sans text-xs uppercase tracking-[2px] text-terracotta">
              What I Bring
            </p>
            <div className="mt-2 w-5 h-[2px] bg-teal/60" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              ["Project Leadership", "Primary client contact, consultant coordination, CA support, and decision tracking from concept to closeout."],
              ["Technical Rigor", "Revit documentation, code-aware planning, finish specifications, detailing, and QA/QC with design intent intact."],
              ["Design + Delivery", "Concept direction, FF&E strategy, procurement coordination, value engineering, and install-phase problem solving."],
            ].map(([title, body]) => (
              <div key={title} className="bg-white border border-teal/10 rounded-xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300">
                <h3 className="font-serif text-xl text-charcoal mb-3">{title}</h3>
                <p className="font-sans text-sm text-charcoal/70 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Section Divider 2 ── */}
      <div className="px-6 md:px-12 lg:px-20 bg-off-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div
            ref={divider2}
            style={{ width: "0%" }}
            className="h-px bg-gradient-to-r from-teal/40 via-terracotta/20 to-transparent"
          />
        </div>
      </div>

      {/* ===== RESUME ===== */}
      <section
        ref={resumeRef}
        id="resume"
        className="bg-off-white pb-16 md:pb-20 px-6 md:px-12 lg:px-20 pt-16 md:pt-20"
      >
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[200px_1fr] gap-8 lg:gap-20">
          {/* Label — slides in from left */}
          <div ref={resumeLabel}>
            <p className="font-sans text-xs uppercase tracking-[2px] text-terracotta">
              Resume
            </p>
            <div className="mt-2 w-5 h-[2px] bg-teal/60" />
          </div>

          {/* Content row — slides up */}
          <div
            ref={resumeRow}
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6 py-6 border-t-2 border-b border-teal/30 border-b-teal/15"
          >
            <p className="font-sans text-sm text-warm-gray leading-relaxed max-w-md">
              Complete work history, education, project experience, and technical expertise.
            </p>
            <a
              href="https://storage.readdy-site.link/project_files/1d9d9d8c-beb2-4287-a085-8aa3612244ba/21318ce6-a213-42f7-943e-cfaff94d23f3_Winter_Spring-2026-Resume_JDuke.pdf?v=25625ba8b0dae8f94dd49d9eaea5ad0d"
              className="inline-flex items-center gap-2 px-7 py-3 bg-terracotta text-white font-sans text-sm font-medium rounded-full hover:bg-terracotta/90 transition-all duration-300 whitespace-nowrap cursor-pointer self-start sm:self-auto"
              download
              target="_blank"
              rel="noopener noreferrer"
            >
              <i className="ri-download-line text-sm" />
              View Resume
            </a>
          </div>
        </div>
      </section>

      {/* ── Section Divider 3 ── */}
      <div className="px-6 md:px-12 lg:px-20 bg-off-white overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div
            ref={divider3}
            style={{ width: "0%" }}
            className="h-px bg-gradient-to-r from-teal/40 via-terracotta/20 to-transparent"
          />
        </div>
      </div>

      {/* ── Divider before Projects ── */}
      <div className="px-6 md:px-12 lg:px-20 bg-cream overflow-hidden">
        <div className="max-w-6xl mx-auto h-px bg-gradient-to-r from-teal/20 via-terracotta/10 to-transparent" />
      </div>

      {/* ===== PROJECTS ===== */}
      <section
        ref={projectsRef}
        id="projects"
        className="bg-cream py-24 md:py-32 px-6 md:px-12 lg:px-20"
      >
        <div className="max-w-6xl mx-auto">
          {/* Header row */}
          <div className="mb-16 flex items-center gap-4">
            <div ref={projectsLabel}>
              <p className="font-sans text-xs uppercase tracking-[2px] text-terracotta">
                Selected Projects
              </p>
            </div>
            <div className="flex-1 overflow-hidden">
              <div
                ref={projectsLine}
                style={{ width: "0%" }}
                className="h-[2px] bg-gradient-to-r from-teal/50 via-teal/20 to-transparent"
              />
            </div>
          </div>

          {/* Project Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {allProjects.map((project, index) => (
              <ProjectCard
                key={project.id}
                project={project}
                categorySlug={project.categorySlug}
                index={index}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── Section Divider 4 ── */}
      <div className="px-6 md:px-12 lg:px-20 bg-cream overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <div
            ref={divider4}
            style={{ width: "0%" }}
            className="h-px bg-gradient-to-r from-teal/40 via-terracotta/20 to-transparent"
          />
        </div>
      </div>

      {/* ===== OTHER WORK ===== */}
      <section
        ref={otherWorkRef}
        id="other-work"
        className="bg-cream py-16 md:py-20 px-6 md:px-12 lg:px-20"
      >
        <div className="max-w-none mx-auto">
          {/* Header row */}
          <div className="mb-8 md:mb-10 flex items-center gap-4">
            <div ref={otherWorkLabel}>
              <p className="font-sans text-xs uppercase tracking-[2px] text-terracotta">
                Additional Experience
              </p>
            </div>
            <div className="flex-1 overflow-hidden">
              <div
                ref={otherWorkLine}
                style={{ width: "0%" }}
                className="h-[2px] bg-gradient-to-r from-teal/50 via-teal/20 to-transparent"
              />
            </div>
          </div>

          {/* List */}
          <div ref={otherWorkItem} className="space-y-0">
            {otherWorkItems.map((item) => (
              <div
                key={item.id}
                className="group grid grid-cols-1 md:grid-cols-[minmax(300px,1fr)_24px_minmax(210px,0.7fr)_24px_minmax(520px,1.8fr)] md:items-center gap-2 md:gap-6 py-6 md:py-8 border-b border-teal/10 first:border-t transition-colors duration-300 hover:bg-white/40 px-0"
              >
                <a
                  href={item.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-serif text-xl md:text-2xl text-charcoal group-hover:text-terracotta transition-colors duration-300 whitespace-nowrap"
                >
                  {item.title}
                </a>
                <span className="hidden md:block w-2 h-2 rounded-full bg-teal/60 justify-self-center" />
                <p className="font-sans text-sm md:text-base text-warm-gray whitespace-nowrap">
                  {item.location} &middot; {item.year}
                </p>
                {item.description && (
                  <>
                    <span className="hidden md:block w-2 h-2 rounded-full bg-teal/60 justify-self-center" />
                    <p className="font-sans text-sm text-warm-gray whitespace-nowrap overflow-hidden text-ellipsis">
                      {item.description}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div ref={contactRef} id="contact">
        <Footer />
      </div>

    </div>
  );
}